import { Trash01, Plus, Copy04, Container } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import VisualizeUnit from 'src/app/MyQuote/Modals/VisualizeUnit';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import Radio from 'src/components/Radio/Radio';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import { MEASUREMENT } from 'src/constants/common';
import { calculateLinearFootage, formatLinearFootage, getCalculatedFreightClass, getLabelByValue } from 'src/utils/CommonFunctions';

import { quoteDetails as quoteDetailsSelector } from '../../CreateQuote.slice';

const MAX_LINE_ITEMS = 15;

export interface IDimension {
    id: number | null;
    handlingUnit: number | string | undefined;
    handlingUnitNo: number | string | undefined;
    weight: number | string | undefined;
    itemLength: number | string | undefined;
    width: number | string | undefined;
    height: number | string | undefined;
    totalWeight: number | string | undefined;
    isStack: boolean | string | undefined;
    description: string | undefined;
    freightClass: number | string | undefined;
    itemClassForDayross: number | string | undefined;
}

const initDimensionData: IDimension = {
    id: null,
    handlingUnit: 1,
    handlingUnitNo: 0,
    weight: 0,
    itemLength: 0,
    width: 0,
    height: 0,
    totalWeight: 0,
    isStack: true,
    description: undefined,
    freightClass: 0,
    itemClassForDayross: 0,
};

const LineItem = () => {
    const { t } = useTranslation();
    const { handlingUnitOptions, commodityOptions } = useSelector((state: any) => state.basic);
    const quoteDetails = useSelector(quoteDetailsSelector);
    const { control, watch, setValue, trigger, formState, getValues, clearErrors } = useFormContext();
    const { isSubmitted } = formState;
    const [currentData, setCurrentData] = useState<{
        index: number;
        data: any;
    } | null>(null);

    const {
        fields: dimensionsOptionFields,
        append: appendOption,
        remove: removeOption,
    } = useFieldArray({
        control,
        name: 'dimensions',
        keyName: 'uuid',
    });

    const weightMeasurement = watch('weightMeasurement');
    const lengthMeasurement = watch('lengthMeasurement');
    const lengthMeasurementLabel = getLabelByValue(watch('lengthMeasurement'));
    const dimensions = watch('dimensions');
    const primaryEquipmentType = watch('primaryEquipmentType');

    useEffect(() => {
        const calLinearFootage = formatLinearFootage(calculateLinearFootage(dimensions, quoteDetails?.classification, false, MEASUREMENT.WEIGHT1?.value, weightMeasurement)) || null;
        setValue('linearFootage', calLinearFootage);

        if (isSubmitted) {
            trigger('linearFootage');
        }
    }, [
        primaryEquipmentType,
        weightMeasurement,
        JSON.stringify(
            dimensions?.map((item: any) => ({
                handlingUnitNo: item.handlingUnitNo,
                width: item.width,
                height: item.height,
                itemLength: item.itemLength,
                weight: item.weight,
                isStack: item.isStack,
            }))
        ),
    ]);

    const handleOption = (onChange: any, name: string) => (e: any) => {
        onChange(e?.value);
        clearErrors(name);
    };

    const handleRemove = (index: number) => (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length > 1) {
            removeOption(index);

            // Trigger validation after removing if form was already submitted
            if (isSubmitted) {
                // Use setTimeout to trigger after the removal has been processed
                setTimeout(() => {
                    trigger('dimensions');
                }, 0);
            }
        } else {
            clearErrors('dimensions');

            // For LTL, allow removing the last item (show empty state with only Add button)
            if (quoteDetails?.classification === 'ltl') {
                setValue('dimensions', []);
            } else {
                setValue('dimensions', [initDimensionData]);
            }
        }
    };

    const handleAdd = (e?: any) => {
        e?.stopPropagation();

        if (dimensionsOptionFields.length >= MAX_LINE_ITEMS) {
            return;
        }

        const newOption: IDimension = initDimensionData;
        appendOption(newOption);
    };

    const handleDuplicate = (index: number) => (e: any) => {
        e.stopPropagation();

        if (dimensionsOptionFields.length >= MAX_LINE_ITEMS) {
            return;
        }

        const fieldValues = watch(`dimensions.${index}`);
        appendOption({ ...fieldValues, id: null });
    };

    const handleInputChange = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${name}`);

        // Clear errors for both weight and totalWeight when either changes
        if (name === 'weight' || name === 'totalWeight') {
            clearErrors(`dimensions.${index}.weight`);
            clearErrors(`dimensions.${index}.totalWeight`);
        }

        if (type === 'number') {
            value = value ? parseFloat(value) : eventType === 'blur' ? 0 : value;
        }

        let totalWeight = lineItemData.totalWeight || 0;
        let weight = lineItemData.weight || 0;
        let handlingUnitNo = lineItemData.handlingUnitNo || 0;

        // If handlingUnitNo or weight changes → update totalWeight = handlingUnitNo * weight
        if (name === 'handlingUnitNo') {
            handlingUnitNo = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        } else if (name === 'weight') {
            weight = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        } else if (name === 'totalWeight') {
            // If totalWeight changes → update weight = totalWeight / handlingUnitNo
            totalWeight = typeof value === 'number' ? value : parseFloat(value) || 0;

            if (handlingUnitNo > 0 && totalWeight > 0) {
                weight = totalWeight / handlingUnitNo;

                if (Number.isInteger(weight)) {
                    weight = parseInt(weight);
                } else {
                    weight = Math.floor(weight * 100) / 100;
                }
            } else {
                weight = 0;
            }
        }

        // Handle legacy 'weight' field for backward compatibility
        if (name === 'weight') {
            weight = value;

            if (handlingUnitNo > 0 && weight > 0) {
                totalWeight = handlingUnitNo * weight;

                if (Number.isInteger(totalWeight)) {
                    totalWeight = parseInt(totalWeight);
                } else {
                    totalWeight = Math.floor(totalWeight * 100) / 100;
                }
            } else {
                totalWeight = 0;
            }
        }

        if (isNaN(totalWeight) || !isFinite(totalWeight)) {
            totalWeight = 0;
        }

        if (isNaN(weight) || !isFinite(weight)) {
            weight = 0;
        }

        // Ensure totalWeight is a number before calling toFixed
        const totalWeightNumber = typeof totalWeight === 'number' ? totalWeight : parseFloat(totalWeight) || 0;

        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.totalWeight`, parseFloat(totalWeightNumber.toFixed(2)));
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);
        // Also update weight field for backward compatibility
        setValue(`dimensions.${index}.weight`, typeof weight === 'number' && !isNaN(weight) ? parseFloat(weight.toFixed(2)) : weight || 0);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
            trigger(`dimensions.${index}.totalWeight`);
            trigger(`dimensions.${index}.weight`);
        }
    };

    const handleChangeWithErrorClear = (index: number, fieldName: string) => (onChange: any) => (e: any) => {
        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${fieldName}`);
        // Handle SelectBox case where value is in e.value
        onChange(e?.value !== undefined ? e.value : e);

        if (isSubmitted) {
            trigger(`dimensions.${index}.${fieldName}`);
        }
    };

    const checkFreightClass = (index: number) => (event: any) => {
        const lineItemData = watch(`dimensions.${index}`);
        const { target, type: eventType } = event;
        let { name, value, type } = target;

        // Clear error for this field when user starts typing
        clearErrors(`dimensions.${index}.${name}`);

        value = type === 'number' ? (value ? parseFloat(value) : eventType === 'blur' ? 0 : value) : value;
        let freightClass = lineItemData.freightClass;
        let itemClassForDayross = lineItemData.itemClassForDayross;
        let weight = lineItemData.weight;
        let width = lineItemData.width;
        let height = lineItemData.height;
        let itemLength = lineItemData.itemLength;

        if (weight && width && itemLength && height) {
            const freightData = getCalculatedFreightClass({
                itemLength: itemLength || 0,
                width: width || 0,
                height: height || 0,
                weight: weight || 0,
                lengthUnit: lengthMeasurement,
                weightUnit: weightMeasurement,
            });

            freightClass = freightData?.freightClass;
            itemClassForDayross = freightData?.itemClassForDayross;
        } else {
            freightClass = 0;
        }
        setValue(`dimensions.${index}.${name}`, value);
        setValue(`dimensions.${index}.freightClass`, freightClass);
        setValue(`dimensions.${index}.itemClassForDayross`, itemClassForDayross);

        if (freightClass) {
            clearErrors(`dimensions.${index}.freightClass`);
        }

        if (isSubmitted) {
            trigger(`dimensions.${index}.${name}`);
        }
    };

    const handleVisualizeUnit = (index: number) => (e: any) => {
        e.stopPropagation();
        setCurrentData({
            ...{ index: index, data: getValues(`dimensions.${index}`) },
        });
    };

    const handleVisualizeUnitClose = () => {
        setCurrentData(null);
    };

    const handleVisualizeUnitSubmit = (index: number, data: any) => {
        const fieldArray = ['itemLength', 'width', 'height', 'handlingUnit'];
        fieldArray.forEach((field) => {
            setValue(`dimensions.${index}.${field}`, data[field]);
        });
        handleVisualizeUnitClose();
    };

    const handleMeasurementChange = (onChange: any) => (event: any) => {
        const { value } = event.target;

        if (value === MEASUREMENT.WEIGHT1.value) {
            setValue('lengthMeasurement', MEASUREMENT.LENGTH1.value);
        } else {
            setValue('lengthMeasurement', MEASUREMENT.LENGTH2.value);
        }
        onChange(value);
    };

    return (
        <>
            <div className="space-y-5 w-full">
                <div className="stepper-content-container ">
                    <div className="stepper-lable-wrap">
                        <label className="stepper-label-common">{t('Common.Dimensions')}</label>
                    </div>

                    <div className="flex items-center gap-x-4 justify-end flex-1">
                        <Controller
                            control={control}
                            name="weightMeasurement"
                            render={({ field: { onChange, value } }) => (
                                <div className="flex items-center gap-4">
                                    <Radio
                                        onChangeFunc={handleMeasurementChange(onChange)}
                                        id="lbs"
                                        value={MEASUREMENT.WEIGHT1.value}
                                        checked={value === MEASUREMENT.WEIGHT1.value}
                                        labelName={t('Common.Imperial_(lbs)')}
                                        labelHtmlFor="lbs"
                                        radioLabelClassName="!normal-case"
                                    />

                                    <Radio
                                        onChangeFunc={handleMeasurementChange(onChange)}
                                        checked={value === MEASUREMENT.WEIGHT2.value}
                                        value={MEASUREMENT.WEIGHT2.value}
                                        id="kgs"
                                        labelName={t('Common.Metric_(kgs)')}
                                        labelHtmlFor="kgs"
                                        radioLabelClassName="normal-case"
                                    />
                                </div>
                            )}
                        />
                    </div>
                </div>

                {dimensionsOptionFields.length === 0 ? (
                    <div className="field-without-top-border rounded-lg gap-x-4 w-full justify-center flex-col items-center ">
                        <div className="border rounded-xl w-full border-gray500 border-dashed items-center bg-white flex justify-center min-h-[123px]">
                            <ButtonCmp className="btn_secondary_black" onClick={handleAdd} icon={<Plus className="w-4 h-4" />}>
                                {t('Common.Add_Line_Items')}
                            </ButtonCmp>
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col gap-y-4">
                            {dimensionsOptionFields.map((item: any, index) => (
                                <div className={`flex xl:flex-row flex-col animate-field-item`} key={item.uuid}>
                                    <div className={`stepper-content-container`}>
                                        <div className="rounded-lg overflow-hidden border border-utilityGray200">
                                            <div className="bg-utilityGray300 flex items-center gap-x-2 p-3 border-b border-utilityGray200">
                                                <label className="text-grayLight900 text-xs font-medium flex-1">
                                                    {t('Common.Line_Item')} {index + 1}
                                                </label>
                                                <TooltipCmp message={t('Common.Duplicate')} childrenClassName="!flex">
                                                    <ButtonCmp className="btn-link-secondary" size="none" onClick={handleDuplicate(index)} disabled={dimensionsOptionFields.length >= MAX_LINE_ITEMS}>
                                                        <Copy04 className="w-4 h-4" />
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                                <TooltipCmp message={t('Common.Visualize_Unit')} childrenClassName="!flex">
                                                    <ButtonCmp className="btn-link-secondary" size="none" onClick={handleVisualizeUnit(index)}>
                                                        <Container className="w-4 h-4" />
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                                <TooltipCmp message={t('Common.Delete')} childrenClassName="!flex">
                                                    <ButtonCmp className="btn-link-secondary" size="none" onClick={handleRemove(index)}>
                                                        <Trash01 className="w-4 h-4" />
                                                    </ButtonCmp>
                                                </TooltipCmp>
                                            </div>
                                            <div className="w-full stepper-lable-content-wrap p-3 flex flex-wrap gap-y-3 gap-x-3 [&>*]:w-[calc(20%_-_10px)]">
                                                <Controller
                                                    name={`dimensions.${index}.handlingUnit`}
                                                    control={control}
                                                    render={({ field: { value, onChange, name }, fieldState: { error } }) => (
                                                        <SelectBox
                                                            label={t('Common.Handling_unit')}
                                                            required
                                                            labelClassName="form_label block mb-1.5"
                                                            name="handlingUnit"
                                                            id="handlingUnit"
                                                            className={`form_control`}
                                                            placeholder={t('Common.Handling_unit')}
                                                            options={handlingUnitOptions}
                                                            onChangeFunc={handleOption(onChange, name)}
                                                            isError={!!error?.message}
                                                            value={handlingUnitOptions.find((handlingUnitOption: any) => handlingUnitOption.value === parseInt(value) || '')}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.handlingUnitNo`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            required
                                                            label={t('Orders.NE_of_HU')}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="handlingUnitNo"
                                                            inputType="number"
                                                            maxLength={3}
                                                            isLoading={false}
                                                            isAllowDecimal={false}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={(e: any) => {
                                                                if (!e.target.value) {
                                                                    onChange(0);
                                                                }
                                                            }}
                                                            isError={!!error?.message}
                                                            shouldFormatNumber={true}
                                                            placeholder="0"
                                                            value={value || ''}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.itemLength`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Length')}
                                                                    &nbsp;(
                                                                    {lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="itemLength"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            required
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.width`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Width')}
                                                                    &nbsp;(
                                                                    {lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="width"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.height`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Height')}
                                                                    &nbsp;(
                                                                    {lengthMeasurementLabel?.toLowerCase()})
                                                                </>
                                                            }
                                                            isLoading={false}
                                                            labelClassName="form_label block mb-1.5"
                                                            inputType="number"
                                                            inputName="height"
                                                            placeholder="0"
                                                            isAllowDecimal={true}
                                                            maxLength={4}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={lengthMeasurementLabel?.toLowerCase()}
                                                            value={value || ''}
                                                            onChangeFunc={handleChangeWithErrorClear(index, 'height')(onChange)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.weight`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('QuotingHub.QuotingDashboard.Unit_Weight')}
                                                                    &nbsp;(
                                                                    {weightMeasurement?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="weight"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            isAllowDecimal={true}
                                                            maxLength={7}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.totalWeight`}
                                                    control={control}
                                                    render={({ field: { value }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={
                                                                <>
                                                                    {t('Common.Total_Weight')}
                                                                    &nbsp;(
                                                                    {weightMeasurement?.toLowerCase()})
                                                                </>
                                                            }
                                                            labelClassName="form_label block mb-1.5"
                                                            inputName="totalWeight"
                                                            inputType="number"
                                                            placeholder="0"
                                                            value={value || ''}
                                                            onChangeFunc={handleInputChange(index)}
                                                            onBlur={checkFreightClass(index)}
                                                            required
                                                            errorText={error?.type === 'max' ? error?.message : ''}
                                                            isIconError={!!error?.message}
                                                            isAllowDecimal={true}
                                                            maxLength={7}
                                                            shouldFormatNumber={true}
                                                            inputValueUnitType={weightMeasurement?.toLowerCase()}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.freightClass`}
                                                    control={control}
                                                    render={({ field: { value, onChange }, fieldState: { error } }) => (
                                                        <InputText
                                                            label={t('QuotingHub.Common.Freight_Class')}
                                                            labelClassName="form_label block mb-1.5"
                                                            isLoading={false}
                                                            inputName="freightClass"
                                                            inputType="number"
                                                            value={value || ''}
                                                            placeholder="0"
                                                            maxLength={3}
                                                            required
                                                            onChangeFunc={handleChangeWithErrorClear(index, 'freightClass')(onChange)}
                                                            shouldFormatNumber={true}
                                                            className={`${error?.message ? 'border border-red-500 border-solid' : ''}`}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.commodity`}
                                                    control={control}
                                                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                                                        <SelectBox
                                                            parentClassName="!w-[calc(40%_-_6.4px)]"
                                                            placeholder={t('Common.Select_commodity')}
                                                            autoCompleteSelect={true}
                                                            name="commodity"
                                                            id={`commodity-${index}`}
                                                            className="form_control"
                                                            label={t('Common.Commodity')}
                                                            labelClassName="form_label block mb-1.5"
                                                            isSearchable={true}
                                                            options={commodityOptions || []}
                                                            value={
                                                                value
                                                                    ? commodityOptions?.find((e: any) => e?.value == value) || {
                                                                          value: value,
                                                                          label: value,
                                                                      }
                                                                    : null
                                                            }
                                                            onChangeFunc={handleChangeWithErrorClear(index, 'commodity')(onChange)}
                                                            isClearable={true}
                                                            onCreateOption={(e: any) => {
                                                                onChange(e);
                                                            }}
                                                            errorText={error?.message ? true : false}
                                                            isShowErrorMsg={false}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`dimensions.${index}.isStack`}
                                                    control={control}
                                                    render={({ field: { value, onChange } }) => (
                                                        <Toggle
                                                            size="xs"
                                                            isChecked={value}
                                                            onChange={onChange}
                                                            labelClassName={`flex items-center`}
                                                            name="isStack"
                                                            label={'Do Not Stack'}
                                                            labelPosition="left"
                                                            labelTextClassName={`!text-xs text-nowrap`}
                                                            id={`doNotStack-${index}`}
                                                        />
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex gap-x-3 w-full justify-end !mt-4 items-center stepper-content-container field-without-top-border animate-field-item">
                            <ButtonCmp className="btn-outline-primary" icon={<Plus className="w-4 h-4" />} onClick={handleAdd} disabled={dimensionsOptionFields.length >= MAX_LINE_ITEMS}>
                                {t('Common.Add_Line_Item')}
                            </ButtonCmp>
                        </div>
                    </>
                )}
            </div>
            {currentData && (
                <VisualizeUnit
                    currentData={currentData}
                    handleClose={handleVisualizeUnitClose}
                    weightMeasurement={weightMeasurement}
                    lengthMeasurement={lengthMeasurement}
                    isEnable={true}
                    parentFreightClass={getValues(`dimensions.${currentData.index}.freightClass`)}
                    unitArr={handlingUnitOptions}
                    isFormDataHaveKey={true}
                    handleVisualizeUnitSubmit={handleVisualizeUnitSubmit}
                />
            )}
        </>
    );
};

export default LineItem;
